import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Building2,
  Search,
  Users,
  FolderKanban,
  ExternalLink,
  Star,
} from 'lucide-react';
import { useState } from 'react';
import { organizationsApi } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeader, Skeleton } from '@solid/ui';
import { cn } from '@/lib/utils';

const ACCOUNTS_URL = 'https://accounts.soliddigital.com';

export function Clients() {
  const [searchQuery, setSearchQuery] = useState('');
  const queryClient = useQueryClient();

  const { data: clients, isLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: () => organizationsApi.list({ type: 'CLIENT', all: true }),
  });

  const favoriteMutation = useMutation({
    mutationFn: ({ id, isFavorited }: { id: string; isFavorited: boolean }) =>
      isFavorited ? organizationsApi.unfavoriteOrg(id) : organizationsApi.favoriteOrg(id),
    onMutate: async ({ id, isFavorited }) => {
      await queryClient.cancelQueries({ queryKey: ['clients'] });
      const previousClients = queryClient.getQueryData<any[]>(['clients']);
      if (previousClients) {
        const updated = previousClients.map((c: any) =>
          c.id === id ? { ...c, isFavorited: !isFavorited } : c
        );
        queryClient.setQueryData(['clients'], updated);
      }
      return { previousClients };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousClients) {
        queryClient.setQueryData(['clients'], context.previousClients);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });

  const filteredClients = clients?.filter((c) =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  )?.sort((a, b) => {
    if (a.isFavorited !== b.isFavorited) return a.isFavorited ? -1 : 1;
    if (a.isFavorited && b.isFavorited) return a.name.localeCompare(b.name);
    return 0;
  });
  
  return (
    <div className="w-full">
      <PageHeader
        title="Organizations"
        subtitle="Manage your organizations and their projects"
      />
      
      {/* Search */}
      <div className="relative max-w-md mb-8">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-grey-400" />
        <input
          type="search"
          data-testid="organizations-search"
          aria-label="Search organizations"
          placeholder="Search organizations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="input pl-10"
        />
      </div>
      
      {/* Clients Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-48 rounded-xl" />
          ))}
        </div>
      ) : filteredClients?.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="No organizations found"
          description={searchQuery 
            ? "Try adjusting your search terms" 
            : "Organizations are managed through the accounts platform"
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredClients?.map((client) => (
            <Card key={client.id} className="flex flex-col h-full hover:shadow-md transition-shadow group">
              <div className="flex items-start justify-between mb-4">
                <Link
                  to={`/projects?organizationId=${client.id}`}
                  className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                >
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center overflow-hidden">
                    {client.logo ? (
                      <img src={client.logo} alt={client.name} className="w-12 h-12 object-cover" />
                    ) : (
                      <Building2 className="w-6 h-6 text-accent" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-heading-4 text-secondary">{client.name}</h3>
                    <p className="text-xs text-grey-400 uppercase tracking-wider font-semibold">
                      {client.slug}
                    </p>
                  </div>
                </Link>
                <div className="flex items-center gap-1">
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); favoriteMutation.mutate({ id: client.id, isFavorited: !!client.isFavorited }); }}
                    className={cn('p-1 rounded transition-all', client.isFavorited ? 'text-yellow-400 opacity-100' : 'text-grey-300 opacity-0 group-hover:opacity-100 hover:text-yellow-400')}
                    title={client.isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                  >
                    <Star className={cn('w-4 h-4', client.isFavorited && 'fill-yellow-400')} />
                  </button>
                  <a
                    href={`${ACCOUNTS_URL}/admin/organizations/${client.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-icon p-1 text-grey-400 hover:text-secondary"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <Link 
                  to={`/projects?organizationId=${client.id}`}
                  className="bg-grey-50 rounded-lg p-3 text-left hover:bg-accent/5 transition-colors group"
                >
                  <div className="flex items-center gap-2 text-grey-500 mb-1 group-hover:text-accent transition-colors">
                    <FolderKanban className="w-3.5 h-3.5" />
                    <span className="text-xs font-medium uppercase tracking-wider">Projects</span>
                  </div>
                  <p className="text-heading-3 text-secondary group-hover:text-accent transition-colors">
                    {client._count?.projects || 0}
                  </p>
                </Link>
                <div className="bg-grey-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-grey-500 mb-1">
                    <Users className="w-3.5 h-3.5" />
                    <span className="text-xs font-medium uppercase tracking-wider">Members</span>
                  </div>
                  <p className="text-heading-3 text-secondary">{client._count?.members || 0}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
